import { useState } from 'react';

import { Box, Button, Grid } from '@mui/material';
import { useQuery } from 'react-query';
import csvSvg from 'src/assets/imgs/csv.svg';
import { PanelLogin } from 'src/components/PanelLogin';
import SubTitle from 'src/components/v2/SubTitle';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  getFIIWalletCRICRA,
  getFIIWalletFIDC,
  getFIIWalletFII,
  getFIIWalletProperty,
} from 'src/services/operation';

import WalletFII from './WalleltFII';
import WalletCRICRA from './WalletCRICRA';
import WalletFIDC from './WalletFIDC';
import WalletProperty from './WalletProperty';
import {
  buildFiiWalletCsv,
  buildFiiWalletSections,
  getDisplayedPropertyRows,
  getFiiWalletFilename,
} from './exportUtils';
import {
  FiiWalletData,
  WalletCRICRARow,
  WalletFIDCRow,
  WalletFIIRow,
  WalletPropertyRow,
} from './types';

interface OperationFIIWalletProps {
  operationCnpj: string;
}

function OperationFIIWallet({
  operationCnpj,
}: OperationFIIWalletProps): JSX.Element | null {
  const { auth } = useAuthContext();
  const canLoadWallet =
    auth.isLoggedIn && auth?.user?.subscription?.plan?.type !== 'free';
  const hasPermission =
    !!auth?.user?.subscription?.plan?.permissions?.operacoes_fii_ativo_carteira;
  const [hasData, setHasData] = useState({
    cri: true,
    fii: true,
    fiiProperty: true,
    fidc: true,
  });

  const criCraQuery = useQuery<WalletCRICRARow[]>(
    ['operation-wallet-cri-cra', operationCnpj],
    () => getFIIWalletCRICRA({ cnpj: operationCnpj }),
    {
      onSuccess: (data) =>
        setHasData((prev) => ({ ...prev, cri: data.length > 0 })),
      enabled: canLoadWallet && !!operationCnpj,
      retry: false,
      useErrorBoundary: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      cacheTime: 0,
      retryOnMount: false,
    },
  );

  const fiiQuery = useQuery<WalletFIIRow[]>(
    ['operation-wallet-fii', operationCnpj],
    () => getFIIWalletFII({ cnpj: operationCnpj }),
    {
      onSuccess: (data) =>
        setHasData((prev) => ({ ...prev, fii: data.length > 0 })),
      enabled: canLoadWallet && !!operationCnpj,
      retry: false,
      useErrorBoundary: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      cacheTime: 0,
      retryOnMount: false,
    },
  );

  const propertyQuery = useQuery<WalletPropertyRow[]>(
    ['operation-wallet-property', 'ativo-imovel', 'fii', operationCnpj],
    () =>
      getFIIWalletProperty({
        mode: 'meta',
        cnpj: operationCnpj,
        market: 'fii',
        indicator: 'ativo-imovel',
        permission: 'operacoes_fii_ativo_carteira',
      }),
    {
      onSuccess: (data) =>
        setHasData((prev) => ({
          ...prev,
          fiiProperty: data.length > 0,
        })),
      enabled: canLoadWallet && !!operationCnpj,
      retry: false,
      useErrorBoundary: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      cacheTime: 0,
      retryOnMount: false,
    },
  );

  const fidcQuery = useQuery<WalletFIDCRow[]>(
    ['operation-wallet-fidc', operationCnpj],
    () => getFIIWalletFIDC({ cnpj: operationCnpj }),
    {
      onSuccess: (data) =>
        setHasData((prev) => ({ ...prev, fidc: data.length > 0 })),
      enabled: canLoadWallet && !!operationCnpj,
      retry: false,
      useErrorBoundary: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      cacheTime: 0,
      retryOnMount: false,
    },
  );

  const criCraData = criCraQuery.data ?? [];
  const fiiData = fiiQuery.data ?? [];
  const propertyData = getDisplayedPropertyRows(propertyQuery.data);
  const fidcData = fidcQuery.data ?? [];
  const walletData: FiiWalletData = {
    criCra: criCraData,
    fii: fiiData,
    properties: propertyData,
    fidc: fidcData,
  };
  const isAnyQueryLoading =
    criCraQuery.isLoading ||
    fiiQuery.isLoading ||
    propertyQuery.isLoading ||
    fidcQuery.isLoading;
  const hasAnyQueryError =
    criCraQuery.isError ||
    fiiQuery.isError ||
    propertyQuery.isError ||
    fidcQuery.isError;
  const hasDataToExport = Object.values(walletData).some(
    (data) => data.length > 0,
  );
  const isExportDisabled =
    isAnyQueryLoading || hasAnyQueryError || !hasDataToExport;

  const handleExport = (): void => {
    if (isExportDisabled) return;

    const csv = buildFiiWalletCsv(buildFiiWalletSections(walletData));
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = getFiiWalletFilename(operationCnpj);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  if (!hasData.cri && !hasData.fii && !hasData.fidc && !hasData.fiiProperty)
    return null;

  return (
    <Box mt={10}>
      <Box position="relative">
        <SubTitle title="Carteira" />
        {canLoadWallet && hasPermission && (
          <Button
            variant="contained"
            startIcon={<img src={csvSvg} alt="export" />}
            disabled={isExportDisabled}
            onClick={handleExport}
            sx={{ position: 'absolute', right: 0, top: -8 }}>
            Exportar
          </Button>
        )}
      </Box>
      {!canLoadWallet ? (
        <PanelLogin small={undefined} message={undefined} />
      ) : (
        <>
          <Grid item xs={12}>
            <WalletCRICRA data={criCraData} isLoading={criCraQuery.isLoading} />
          </Grid>
          <Grid item xs={12}>
            <WalletFII data={fiiData} isLoading={fiiQuery.isLoading} />
          </Grid>
          <Grid item xs={12}>
            <WalletProperty
              data={propertyData}
              isLoading={propertyQuery.isLoading}
            />
          </Grid>
          <Grid item xs={12}>
            <WalletFIDC data={fidcData} isLoading={fidcQuery.isLoading} />
          </Grid>
        </>
      )}
    </Box>
  );
}

export default OperationFIIWallet;
